import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { saveListContext } from '@/lib/ticket-nav';
import { sessionStore } from '@/lib/browser-storage';
import {
  useTeamMembers,
  useTicketSearchV2,
  useMyWatchedTicketIds,
  useActiveAwayPeriods,
  TICKET_DATE_FIELD_LABELS,
  type TeamMember,
  type TicketDateField,
  type TicketFilters,
} from '@/lib/queries';
import { TicketRow } from '@/components/TicketCard';
import { BulkActionsBar } from '@/components/BulkActionsBar';
import { FyiLaneBar } from '@/components/FyiLaneBar';
import { AssigneeFilter } from '@/components/AssigneeFilter';
import { SavedDashboardViews } from '@/components/SavedDashboardViews';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { useAuth } from '@/lib/auth-guard';
import { useCurrentInbox } from '@/lib/inbox-scope';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmptyState, TicketRowSkeletonList } from '@/components/Skeletons';
import {
  ArrowDownAZ,
  AlarmClock,
  ArrowDownNarrowWide,
  ChevronDown,
  Clock,
  Inbox,
  ListFilter,
  Loader2,
  MessageSquare,
  Search,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { priorityColor } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useKeyboardShortcuts } from '@/lib/use-keyboard-shortcuts';
import { SearchSnippet } from '@/components/SearchSnippet';
import { RecentSearchesMenu } from '@/components/RecentSearchesMenu';
import { SearchSyntaxHelp } from '@/components/SearchSyntaxHelp';
import { GROUPS, parseGroupKey, type GroupKey } from '@/lib/ticket-groups';
import { stillInView } from '@/lib/dashboard-view';
import {
  buildSearchRpcArgs,
  hasSearchContent,
  highlightTermsOf,
  parseSearchQuery,
  stripOperator,
  type SearchMatch,
} from '@/lib/search-query';
import { loadRecent, pushRecent, recentKey, removeRecent, saveRecent } from '@/lib/recent-searches';

const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];
const STATUSES = ['new', 'in_progress', 'waiting_on_client', 'resolved', 'closed', 'escalated'];

// Sort taxonomy. `newest` is the default. The server (search_tickets_v2)
// does the ordering, so the list is ordered across ALL pages, not just the
// rows loaded so far.
type SortKey =
  | 'relevance'
  | 'newest'
  | 'oldest'
  | 'recent_activity'
  | 'priority'
  | 'sla_due'
  | 'subject';

interface SortOption {
  key: SortKey;
  label: string;
  hint: string;
  icon: typeof Clock;
}

const SORT_OPTIONS: SortOption[] = [
  { key: 'relevance',       label: 'Best match',       hint: 'Most relevant first (search only)', icon: Search },
  { key: 'newest',          label: 'Newest first',     hint: 'Created date · descending', icon: Clock },
  { key: 'oldest',          label: 'Oldest first',     hint: 'Created date · ascending',  icon: Clock },
  { key: 'recent_activity', label: 'Latest reply',     hint: 'Most recent client message',icon: MessageSquare },
  { key: 'priority',        label: 'Priority',         hint: 'P1 first, then P2/P3/P4',   icon: ArrowDownNarrowWide },
  { key: 'sla_due',         label: 'SLA due soonest',  hint: 'First-response timer',      icon: AlarmClock },
  { key: 'subject',         label: 'Subject A–Z',      hint: 'Alphabetical',              icon: ArrowDownAZ },
];

// Parse filters/group from the URL search params. Pulled out of the
// component so both the initial-mount state AND the resync effect below
// (which re-applies a saved view or a /reports drill-down link while the
// dashboard is already mounted) share one implementation.
function parseFiltersFromSearchParams(searchParams: URLSearchParams): TicketFilters {
  const out: TicketFilters = {};
  const splitParam = (k: string) =>
    searchParams.get(k)?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];
  const priorities = splitParam('priorities');
  const statuses = splitParam('statuses');
  const categories = splitParam('categories');
  const assignees = splitParam('assignees');
  if (priorities.length > 0) out.priorities = priorities;
  if (statuses.length > 0) out.statuses = statuses;
  if (categories.length > 0) out.categories = categories;
  if (assignees.length > 0) out.assigneeSlackIds = assignees;
  if (searchParams.get('unread') === '1') out.unread = true;
  const ec = searchParams.get('existingClient');
  if (ec === 'true') out.isExistingClient = true;
  else if (ec === 'false') out.isExistingClient = false;
  const createdFrom = searchParams.get('createdFrom');
  const createdTo = searchParams.get('createdTo');
  const dateFieldParam = searchParams.get('dateField');
  if (createdFrom) out.createdFrom = createdFrom;
  if (createdTo) out.createdTo = createdTo;
  if (dateFieldParam && dateFieldParam in TICKET_DATE_FIELD_LABELS) {
    out.dateField = dateFieldParam as TicketDateField;
  }
  return out;
}

// Inverse of parseFiltersFromSearchParams/parseGroupFromSearchParams — turns
// the LIVE in-memory filter state into the query string a saved view stores.
// Needed because normal filtering (toggling a priority/status/type/assignee
// chip) deliberately never touches the address bar (see the comment where
// `filters`/`group` state is declared) — so `location.search` is NOT a
// reliable snapshot of "what's currently filtered" and must not be used for
// Save. `group` is omitted when it's the default ('open') so a totally
// unfiltered view serializes to '' and round-trips cleanly. The search box
// text (`q`) IS included: it lives in the URL too, so a saved view or a shared
// link brings the search back. Sort order is excluded, matching the existing
// session-local-only treatment of `sortBy`.
function serializeFiltersToSearchParams(
  filters: TicketFilters,
  group: GroupKey,
  q: string = '',
): string {
  const p = new URLSearchParams();
  if (filters.priorities && filters.priorities.length > 0) p.set('priorities', filters.priorities.join(','));
  if (filters.statuses && filters.statuses.length > 0) p.set('statuses', filters.statuses.join(','));
  if (filters.categories && filters.categories.length > 0) p.set('categories', filters.categories.join(','));
  if (filters.assigneeSlackIds && filters.assigneeSlackIds.length > 0) p.set('assignees', filters.assigneeSlackIds.join(','));
  if (typeof filters.isExistingClient === 'boolean') p.set('existingClient', String(filters.isExistingClient));
  if (filters.unread) p.set('unread', '1');
  if (filters.createdFrom) p.set('createdFrom', filters.createdFrom);
  if (filters.createdTo) p.set('createdTo', filters.createdTo);
  if (filters.dateField) p.set('dateField', filters.dateField);
  if (group !== 'open') p.set('group', group);
  if (q.trim()) p.set('q', q.trim());
  return p.toString();
}

function parseGroupFromSearchParams(searchParams: URLSearchParams): GroupKey {
  return parseGroupKey(searchParams.get('group'));
}

const ZERO_COUNTS: Record<GroupKey, number> = {
  open: 0,
  mine: 0,
  team: 0,
  unassigned: 0,
  fyi: 0,
  watching: 0,
  snoozed: 0,
  all: 0,
  archived: 0,
};

export function DashboardRoute() {
  const { teamMember } = useAuth();
  const mySlackId = teamMember?.slack_user_id ?? null;
  // Membership of the "Watching" tab, so a row stays right after an optimistic
  // change. (The rows themselves come from the server.)
  const watchedQ = useMyWatchedTicketIds(mySlackId);

  // Hydrate state from URL search params so drill-down links from /reports
  // (`/dashboard?priorities=P1&categories=billing&group=all`) land with the
  // right filters pre-applied, AND so applying a saved view (which just
  // navigates to a new `?search`) actually takes effect on an
  // already-mounted dashboard. Toggling a filter chip is still purely
  // in-memory and never touches the URL — the resync effect below only
  // re-fires when the query string itself genuinely changes (a real
  // navigation), never from ordinary interactive filtering.
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState<GroupKey>(() => parseGroupFromSearchParams(searchParams));
  const [filters, setFilters] = useState<TicketFilters>(() => parseFiltersFromSearchParams(searchParams));

  // `useSearchParams()` can hand back a new URLSearchParams object on every
  // render, so we key the effect on its string form (stable across renders
  // that don't actually change the query) rather than the object itself —
  // otherwise this would refire (and stomp in-memory filter edits) constantly.
  // The search text (?q=) is excluded from the key: typing writes it to the URL
  // (replace), and that must NOT re-parse the URL and wipe in-memory chips.
  const filterKey = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete('q');
    return p.toString();
  }, [searchParams]);
  useEffect(() => {
    setFilters(parseFiltersFromSearchParams(searchParams));
    setGroup(parseGroupFromSearchParams(searchParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);
  // Sort order — in-memory only. URL synchronisation (and saved-view
  // capture) intentionally not wired yet; matches the rest of the
  // dashboard filter state, which is also session-local after mount.
  // ── Search ────────────────────────────────────────────────────────────────
  // One box. The text lives in the URL (?q=) so a reload, a shared link or a
  // saved view brings it back; the server (search_tickets_v2) does the work,
  // scoped to this inbox and the active tab. See lib/search-query.ts.
  const [q, setQ] = useState(() => searchParams.get('q') ?? '');
  const [committedQ, setCommittedQ] = useState(q);
  useEffect(() => {
    const id = setTimeout(() => setCommittedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);
  const lastWrittenQ = useRef(searchParams.get('q') ?? '');
  useEffect(() => {
    const clean = committedQ.trim();
    if (clean === lastWrittenQ.current) return;
    lastWrittenQ.current = clean;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (clean) next.set('q', clean);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }, [committedQ, setSearchParams]);
  // Back/forward or an applied saved view changed ?q= from outside.
  const urlQ = searchParams.get('q') ?? '';
  useEffect(() => {
    if (urlQ !== lastWrittenQ.current) {
      lastWrittenQ.current = urlQ;
      setQ(urlQ);
      setCommittedQ(urlQ);
    }
  }, [urlQ]);

  const parsedSearch = useMemo(() => parseSearchQuery(committedQ), [committedQ]);
  const isSearching = hasSearchContent(parsedSearch);
  const highlightTerms = useMemo(() => highlightTermsOf(parsedSearch), [parsedSearch]);
  // `is:archived` in the box overrides the tab.
  const activeGroup: GroupKey = parsedSearch.is ?? group;
  const selectGroup = (key: GroupKey) => {
    setGroup(key);
    if (parsedSearch.is) {
      const next = stripOperator(q, 'is');
      setQ(next);
      setCommittedQ(next);
    }
  };

  // Sort: session-local. While searching, "Best match" is the default unless
  // the agent picked something else.
  const [sortChoice, setSortChoice] = useState<SortKey | null>(null);
  const sortBy: SortKey =
    sortChoice && !(sortChoice === 'relevance' && !isSearching)
      ? sortChoice
      : isSearching
        ? 'relevance'
        : 'newest';

  // Recent searches: per agent, in this browser (storage may be blocked).
  const recentStorageKey = recentKey(mySlackId);
  const safeStorage = () => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };
  const [recent, setRecent] = useState<string[]>(() => loadRecent(safeStorage(), recentStorageKey));
  useEffect(() => {
    setRecent(loadRecent(safeStorage(), recentStorageKey));
  }, [recentStorageKey]);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentIdx, setRecentIdx] = useState(-1);
  const rememberSearch = (value: string) => {
    const next = pushRecent(recent, value);
    saveRecent(safeStorage(), recentStorageKey, next);
    setRecent(next);
  };
  const pickRecent = (value: string) => {
    setQ(value);
    setCommittedQ(value);
    rememberSearch(value);
    setRecentOpen(false);
    setRecentIdx(-1);
  };
  const showRecent = recentOpen && q.trim() === '' && recent.length > 0;

  // Multi-select state for bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());


  // This comment used to claim the selection was "cleared on group change so
  // we never accidentally act on rows that scrolled off-screen" — but no such
  // effect existed. Selecting 40 tickets in All open, switching to Unassigned,
  // and hitting Dismiss acted on the 40 you could no longer see. Here is the
  // effect the comment was describing.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [group, filters]);

  // ── The list ──────────────────────────────────────────────────────────────
  // Always read from the server (search_tickets_v2). It applies the tab, the
  // chips and the date range in SQL and returns the tab counts, the per-assignee
  // counts and the rows page by page — so no ticket can fall off a "newest N"
  // page. (This screen used to fetch the 500 newest tickets and filter them
  // here, which hid every older ticket, even one assigned to you with the
  // client still replying.) With nothing typed it is simply "the tab, the chips
  // and the date range"; typing adds the search terms. See CODE_MAP.md
  // "Never miss a ticket".
  //
  // Scope to the current inbox from /inbox/:slug. RLS already gates which
  // inboxes the user can see; the RPC also checks my_inbox_ids().
  const { currentInbox } = useCurrentInbox();
  const { data: teamMembers } = useTeamMembers();

  const searchMembers = useMemo(
    () =>
      (teamMembers ?? []).map((m) => ({
        slack_user_id: m.slack_user_id,
        name: m.name,
        email: m.email,
      })),
    [teamMembers],
  );
  const searchBuilt = useMemo(
    () =>
      buildSearchRpcArgs(parsedSearch, {
        inboxId: currentInbox.id,
        group,
        filters: {
          priorities: filters.priorities,
          statuses: filters.statuses,
          categories: filters.categories,
          assigneeSlackIds: filters.assigneeSlackIds,
          isExistingClient: filters.isExistingClient,
          unread: filters.unread,
          createdFrom: filters.createdFrom,
          createdTo: filters.createdTo,
          dateField: filters.dateField,
        },
        sort: sortBy,
        mySlackId,
        members: searchMembers,
      }),
    [parsedSearch, currentInbox.id, group, filters, sortBy, mySlackId, searchMembers],
  );
  const searchQ = useTicketSearchV2(searchBuilt.args);
  const searchFirst = searchQ.data?.pages[0];
  const searchRows = useMemo(
    () => searchQ.data?.pages.flatMap((p) => p.rows) ?? [],
    [searchQ.data],
  );
  const matchById = useMemo(() => {
    const m = new Map<string, SearchMatch | null>();
    for (const r of searchRows) m.set(r.ticket.id, r.match);
    return m;
  }, [searchRows]);

  const tickets = useMemo(() => searchRows.map((r) => r.ticket), [searchRows]);
  const isLoading = searchQ.isLoading;
  const error = searchQ.error;
  // The search box spinner is for searches; the list dims for a search in
  // flight and while a tab/chip switch is still showing the previous rows.
  const searchBusy = isSearching && searchQ.isFetching;
  const listStale = searchQ.isPlaceholderData;
  const searchNotes = isSearching ? searchBuilt.errors : [];

  // slack_user_id → TeamMember, so rows can show "RE" instead of "U2T5T…".
  const teamMembersBySlackId = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of teamMembers ?? []) map.set(m.slack_user_id, m);
    return map;
  }, [teamMembers]);

  // The tab badges come from the server: they describe the whole inbox (and,
  // while searching or filtering, the matches), not just the rows loaded.
  const counts = searchFirst?.counts ?? ZERO_COUNTS;

  // The paperclip comes with each row from the server.
  const attachmentIds = useMemo(
    () => new Set(searchRows.filter((r) => r.has_attachment).map((r) => r.ticket.id)),
    [searchRows],
  );

  // Active away periods (Map<away_slack_id, AwayPeriod>) for the
  // assignee-avatar "on leave" pip + tooltip. One team-wide query
  // shared across every row.
  const awayQ = useActiveAwayPeriods();
  const awayPeriods = awayQ.data;

  // The server already filtered, grouped and ordered these rows. This check only
  // takes a row out the moment an optimistic click (Resolve, Claim, Snooze…)
  // moves it out of the current tab / chips — before the server has confirmed,
  // so the list closes up at once — and hides the previous tab's rows while a
  // tab switch is still loading. On an unchanged row it keeps it.
  const filtered = useMemo(() => {
    const view = {
      group: activeGroup,
      mySlackId,
      watchedIds: watchedQ.data ?? null,
      priorities: filters.priorities,
      statuses: filters.statuses,
      categories: filters.categories,
      isExistingClient: filters.isExistingClient,
      assigneeSlackIds: filters.assigneeSlackIds,
      unread: filters.unread,
    };
    const now = Date.now();
    return tickets.filter((t) => stillInView(t, view, now));
  }, [tickets, activeGroup, mySlackId, watchedQ.data, filters]);

  // Keep the selection to rows still on screen (a search narrowing the list
  // must not leave hidden rows selected for a bulk action). Typing no longer
  // clears it: search text is not part of `filters`.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filtered.map((t) => t.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  // ── Select-all, for the checkbox at the head of the checkbox column ───────
  // Scoped to what's VISIBLE (`filtered`), not the whole 500-row page — the
  // user is looking at a filtered view, and selecting rows they can't see is
  // how people accidentally dismiss 400 tickets.
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id));
  const someVisibleSelected =
    !allVisibleSelected && filtered.some((t) => selectedIds.has(t.id));
  const selectAllLabel = allVisibleSelected
    ? `Deselect all ${filtered.length} visible tickets`
    : `Select all ${filtered.length} visible tickets`;

  // `indeterminate` is a DOM property, not an attribute — React can't set it
  // declaratively, so it needs a ref. This is what lets the header checkbox
  // show "some of these are selected" instead of lying with an empty box.
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const t of filtered) next.delete(t.id);
      } else {
        for (const t of filtered) next.add(t.id);
      }
      return next;
    });
  };

  // Build per-assignee counts for the current group (so the chip row only
  // shows teammates who actually have visible tickets, with a live count).
  // Excludes the assignee filter itself so toggling a chip doesn't make the
  // chip row collapse to one entry.
  const showAssigneeFilter =
    activeGroup === 'team' || activeGroup === 'open' || activeGroup === 'all' || activeGroup === 'archived';
  const assigneeCounts = useMemo(
    () =>
      showAssigneeFilter
        ? new Map<string, number>(Object.entries(searchFirst?.assignee_counts ?? {}))
        : new Map<string, number>(),
    [showAssigneeFilter, searchFirst],
  );

  const togglePriority = (p: string) => {
    setFilters((f) => {
      const cur = new Set(f.priorities ?? []);
      if (cur.has(p)) cur.delete(p);
      else cur.add(p);
      return { ...f, priorities: [...cur] };
    });
  };

  const toggleStatus = (s: string) => {
    setFilters((f) => {
      const cur = new Set(f.statuses ?? []);
      if (cur.has(s)) cur.delete(s);
      else cur.add(s);
      return { ...f, statuses: [...cur] };
    });
  };

  const filtersActive =
    (filters.priorities?.length ?? 0) > 0 ||
    (filters.statuses?.length ?? 0) > 0 ||
    typeof filters.isExistingClient === 'boolean' ||
    !!filters.unread ||
    (filters.assigneeSlackIds?.length ?? 0) > 0 ||
    (filters.categories?.length ?? 0) > 0 ||
    !!filters.createdFrom ||
    !!filters.createdTo ||
    q.trim() !== '';

  const resetFilters = () => {
    setFilters({});
    setQ('');
    setCommittedQ('');
  };

  // Live snapshot of the current filters/group, in the same string shape a
  // saved view stores — this is what "Save current view" actually captures
  // (not location.search, which normal chip-toggling never updates).
  const currentViewSearch = useMemo(
    () => serializeFiltersToSearchParams(filters, group, committedQ),
    [filters, group, committedQ],
  );
  const clearView = () => {
    resetFilters();
    setGroup('open');
  };

  // Keyboard nav: j/k to walk rows, Enter to open, / to focus search.
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [activeIdx, setActiveIdx] = useState<number>(-1);

  // Reset selection when the visible list changes.
  useEffect(() => {
    if (filtered.length === 0) setActiveIdx(-1);
    else if (activeIdx >= filtered.length) setActiveIdx(filtered.length - 1);
  }, [filtered, activeIdx]);

  useKeyboardShortcuts([
    {
      key: '/',
      handler: (e) => {
        e.preventDefault();
        searchInputRef.current?.focus();
      },
    },
    {
      key: 'j',
      handler: () => {
        setActiveIdx((i) => Math.min(filtered.length - 1, i < 0 ? 0 : i + 1));
      },
    },
    {
      key: 'k',
      handler: () => {
        setActiveIdx((i) => Math.max(0, i < 0 ? 0 : i - 1));
      },
    },
    {
      key: 'Enter',
      handler: () => {
        if (activeIdx >= 0 && activeIdx < filtered.length) {
          navigate(`/tickets/${filtered[activeIdx].id}`);
        }
      },
    },
    {
      key: 'Escape',
      allowInInput: true,
      handler: (e) => {
        // First Escape clears the search text, the next one leaves the box.
        if (document.activeElement === searchInputRef.current) {
          e.preventDefault();
          if (q !== '') {
            setQ('');
            setCommittedQ('');
          } else {
            searchInputRef.current?.blur();
          }
        }
      },
    },
  ]);

  // Scroll the active row into view when navigating with j/k.
  const rowRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  useEffect(() => {
    if (activeIdx < 0) return;
    const el = rowRefs.current.get(activeIdx);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeIdx]);

  // The list is paged by the server. Load the next page as the end comes into
  // view, so the rest of the tickets are simply further down — the "Load more"
  // button below stays as the fallback.
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = searchQ;
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasNextPage || isFetchingNextPage || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void fetchNextPage();
      },
      { rootMargin: '600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
    // filtered.length: re-arm after each page, in case the end is still in view.
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, filtered.length]);

  // Remember this list (ids in order, which tab, how to get back) so a ticket opened
  // from it can show "3 of 57", step with [ and ], and open the next one after Resolve.
  // The tab and chips live in memory, not in the URL, so "back" is built from the same
  // string a saved view stores; that brings the tab and filters back.
  const location = useLocation();
  useEffect(() => {
    if (filtered.length === 0) return;
    const qs = currentViewSearch.replace(/^\?/, '');
    saveListContext(
      {
        ids: filtered.map((t) => t.id),
        label: isSearching ? 'Search results' : (GROUPS.find((g) => g.key === activeGroup)?.label ?? 'Tickets'),
        returnTo: location.pathname + (qs ? '?' + qs : ''),
        hasMore: !!hasNextPage,
      },
      sessionStore(),
    );
  }, [filtered, isSearching, activeGroup, hasNextPage, location.pathname, currentViewSearch]);

  return (
    <div className="flex flex-col gap-4">
      {/* Title + search. Stacks vertically on mobile so the search has full
          width to breathe; horizontal on sm+. The subtitle gives an at-a-
          glance read of the queue depth without scrolling — open-count is
          the number people actually care about. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div className="space-y-0.5">
          <h1 className="text-xl font-semibold tracking-tight">Tickets</h1>
          {isSearching ? (
            <p className="text-2xs text-muted-foreground tabular-nums">
              <span className="font-medium text-foreground/80">
                {searchFirst ? searchFirst.total : '…'}
              </span>{' '}
              {searchFirst?.total === 1 ? 'match' : 'matches'} in this tab
              <span className="px-1.5 text-muted-foreground/70">·</span>
              <span>{counts.all} across all active tabs</span>
            </p>
          ) : (
            <p className="text-2xs text-muted-foreground tabular-nums">
              <span className="font-medium text-foreground/80">{counts.open}</span> open
              <span className="px-1.5 text-muted-foreground/70">·</span>
              <span>{counts.mine} mine</span>
              <span className="px-1.5 text-muted-foreground/70">·</span>
              <span>{counts.all} total</span>
            </p>
          )}
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <SortDropdown sortBy={sortBy} onChange={setSortChoice} showRelevance={isSearching} />
          <SavedDashboardViews currentSearch={currentViewSearch} onClear={clearView} />
        </div>
        <div className="relative w-full sm:w-80">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              setCommittedQ(q);
              rememberSearch(q);
              setRecentOpen(false);
            }}
          >
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              aria-label="Search tickets"
              placeholder='Search tickets, messages, notes…'
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                // Emptying the box brings the recent searches back, even though
                // it never lost focus (Enter and the clear button keep it).
                if (e.target.value.trim() === '') setRecentOpen(true);
              }}
              onClick={() => setRecentOpen(true)}
              onFocus={() => {
                setRecentOpen(true);
                setRecentIdx(-1);
              }}
              onBlur={() => setRecentOpen(false)}
              onKeyDown={(e) => {
                if (!showRecent) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setRecentIdx((i) => Math.min(recent.length - 1, i + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setRecentIdx((i) => Math.max(-1, i - 1));
                } else if (e.key === 'Enter' && recentIdx >= 0 && recent[recentIdx]) {
                  e.preventDefault();
                  pickRecent(recent[recentIdx]);
                }
              }}
              className="h-8 pl-7 pr-[4.25rem] text-xs"
            />
            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
              {searchBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Searching" />
              ) : null}
              {q !== '' ? (
                <button
                  type="button"
                  onClick={() => {
                    setQ('');
                    setCommittedQ('');
                    setRecentOpen(true);
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
              <SearchSyntaxHelp
                onInsert={(text) => {
                  setQ((cur) => (cur.trim() ? `${cur.trim()} ${text}` : text));
                  searchInputRef.current?.focus();
                }}
              />
            </div>
          </form>
          {showRecent ? (
            <RecentSearchesMenu
              items={recent}
              activeIndex={recentIdx}
              onPick={pickRecent}
              onRemove={(value) => {
                const next = removeRecent(recent, value);
                saveRecent(safeStorage(), recentStorageKey, next);
                setRecent(next);
              }}
              onClear={() => {
                saveRecent(safeStorage(), recentStorageKey, []);
                setRecent([]);
              }}
            />
          ) : null}
        </div>
      </div>

      {/* Grouping tabs — GitHub PR style underline with Linear-grade polish.
          On mobile (≤sm), horizontally scrollable so 6 tabs don't wrap or
          clip. Scrollbar hidden for a cleaner look; users swipe like Slack
          channel rows. Each tab is `whitespace-nowrap shrink-0` so labels
          like "All open" never break to two lines. */}
      <div className="-mx-4 flex items-center gap-5 overflow-x-auto border-b border-border/70 px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        {GROUPS.map((g) => {
          const active = g.key === activeGroup;
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => selectGroup(g.key)}
              className={cn(
                'group/tab relative -mb-px flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap px-0.5 pb-2.5 pt-1.5 text-sm transition-colors',
                active
                  ? 'font-semibold text-foreground'
                  : 'font-medium text-muted-foreground hover:text-foreground/85',
              )}
            >
              <span>{g.label}</span>
              <span
                className={cn(
                  'inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-1.5 text-3xs font-semibold tabular-nums leading-none transition-colors',
                  active
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground group-hover/tab:bg-muted/80',
                )}
              >
                {counts[g.key]}
              </span>
              {active ? (
                <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-foreground" />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Filter bar — grouped pills with subtle labels.
          Three groups separated by a soft divider:
            1. Priority — color-coded when active (red P1 → slate P4) so the
               selected level is instantly readable at a glance.
            2. Status — neutral pills; using semantic color here would
               compete with priority and double-color the row.
            3. Type / Assignee — narrowing affordances.
          The whole row sits on a faint bg + border so the filter affordance
          reads as a distinct surface from the list below, without making it
          feel heavy.
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
        <FilterIcon />

        <FilterGroup label="Priority">
          {PRIORITIES.map((p) => {
            const active = filters.priorities?.includes(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePriority(p)}
                className={cn(
                  'inline-flex h-6 cursor-pointer items-center justify-center rounded-md border px-2 text-2xs font-semibold tabular-nums transition-colors',
                  active
                    ? // Use the priority's semantic color so the active level
                      // is identifiable at a glance — no need to read the
                      // label.
                      `border-transparent ${priorityColor(p)}`
                    : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {p}
              </button>
            );
          })}
        </FilterGroup>

        <FilterDivider />

        <FilterGroup label="Status">
          {STATUSES.map((s) => {
            const active = filters.statuses?.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={cn(
                  'inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-2xs font-medium transition-colors',
                  active
                    ? 'border-transparent bg-foreground text-background'
                    : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {s.replace(/_/g, ' ')}
              </button>
            );
          })}
        </FilterGroup>

        <FilterDivider />

        <FilterGroup label="Date">
          <DateRangeFilter
            from={filters.createdFrom}
            to={filters.createdTo}
            dateField={filters.dateField}
            onApply={(r) =>
              setFilters((f) => ({
                ...f,
                createdFrom: r.from,
                createdTo: r.to,
                dateField: r.dateField,
              }))
            }
            onClear={() =>
              // Clearing the range also resets the field — picking
              // "Resolved" but having no range applied is a no-op state
              // that's confusing to leave dangling.
              setFilters((f) => ({
                ...f,
                createdFrom: undefined,
                createdTo: undefined,
                dateField: undefined,
              }))
            }
          />
        </FilterGroup>

        <FilterDivider />

        <FilterGroup label="Type">
          <button
            type="button"
            onClick={() =>
              setFilters((f) => ({
                ...f,
                isExistingClient: f.isExistingClient === true ? undefined : true,
              }))
            }
            className={cn(
              'inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-2xs font-medium transition-colors',
              filters.isExistingClient === true
                ? 'border-transparent bg-tint-success text-tint-success-fg ring-1 ring-inset ring-tint-success-ring'
                : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            Client
          </button>
          <button
            type="button"
            onClick={() =>
              setFilters((f) => ({
                ...f,
                isExistingClient: f.isExistingClient === false ? undefined : false,
              }))
            }
            className={cn(
              'inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-2xs font-medium transition-colors',
              filters.isExistingClient === false
                ? 'border-transparent bg-tint-info text-tint-info-fg ring-1 ring-inset ring-tint-info-ring'
                : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            Prospect
          </button>
        </FilterGroup>

        <FilterDivider />

        {/* Unread: tickets where the client wrote since you last opened them. The number
            is how many there are in this tab (it ignores the chip itself). */}
        <FilterGroup label="Show">
          <button
            type="button"
            onClick={() => setFilters((f) => ({ ...f, unread: f.unread ? undefined : true }))}
            aria-pressed={!!filters.unread}
            className={cn(
              'inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-2xs font-medium transition-colors',
              filters.unread
                ? 'border-transparent bg-tint-info text-tint-info-fg ring-1 ring-inset ring-tint-info-ring'
                : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            Unread
            {(searchFirst?.unread ?? 0) > 0 ? (
              <span className="tabular-nums">{searchFirst?.unread}</span>
            ) : null}
          </button>
        </FilterGroup>

        {/* Category filter — only renders when one is active (typically set
            via drill-down from /reports). No "pick a category" UI yet; the
            chip just lets the user remove the filter once they've drilled in. */}
        {filters.categories && filters.categories.length > 0 ? (
          <>
            <FilterDivider />
            <FilterGroup label="Category">
              {filters.categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      categories: (f.categories ?? []).filter((x) => x !== c),
                    }))
                  }
                  className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-md border border-transparent bg-tint-active px-2 text-2xs font-medium text-tint-active-fg ring-1 ring-inset ring-tint-active-ring transition-colors hover:bg-tint-active-ring"
                  title={`Remove category filter: ${c}`}
                >
                  {c}
                  <span aria-hidden className="text-tint-active-fg/70">×</span>
                </button>
              ))}
            </FilterGroup>
          </>
        ) : null}

        {showAssigneeFilter && assigneeCounts.size > 0 ? (
          <>
            <FilterDivider />
            <AssigneeFilter
              assigneeCounts={assigneeCounts}
              teamMembersBySlackId={teamMembersBySlackId}
              selected={filters.assigneeSlackIds ?? []}
              onChange={(next) =>
                setFilters((f) => ({
                  ...f,
                  assigneeSlackIds: next.length > 0 ? next : undefined,
                }))
              }
              mySlackId={mySlackId}
            />
          </>
        ) : null}

        {filtersActive ? (
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto inline-flex h-6 items-center gap-1 rounded-md px-2 text-2xs font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          >
            Clear all
          </button>
        ) : null}
      </div>


      {error ? (
        <div className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <span className="min-w-0 flex-1">
            {isSearching ? 'Search failed: ' : 'Could not load tickets: '}
            {(error as Error).message}
          </span>
          <Button size="sm" variant="outline" onClick={() => void searchQ.refetch()}>
            Retry
          </Button>
        </div>
      ) : null}
      {searchNotes.length > 0 ? (
        <div className="rounded-md border border-tint-warning-ring bg-tint-warning px-3 py-2 text-2xs text-tint-warning-fg">
          {searchNotes.join(' ')}
        </div>
      ) : null}

      {/* List header — select-all lives in the SAME column as the row
          checkboxes (pl-3 matches TicketRow's checkbox inset), because a
          control that selects every row belongs at the head of the column it
          acts on. It used to be a text link on the far right of this strip,
          about 900px away from the checkboxes it toggled.

          It's also a real checkbox now rather than a "Select all"/"Deselect
          all" text swap, so its state is visible at a glance and it can show
          the indeterminate (some-but-not-all) state that a label never could. */}
      {!isLoading && filtered.length > 0 ? (
        <div className="flex items-center gap-3 border-b border-border/60 pb-2 pl-3 pr-1 text-2xs text-muted-foreground">
          <label className="flex shrink-0 cursor-pointer items-center" title={selectAllLabel}>
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
              aria-label={selectAllLabel}
              className="h-4 w-4 cursor-pointer accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            />
          </label>
          <span className="tabular-nums">
            {isSearching
              ? `${filtered.length} of ${searchFirst?.total ?? filtered.length} match${(searchFirst?.total ?? filtered.length) === 1 ? '' : 'es'}`
              : `${(searchFirst?.total ?? 0) > filtered.length ? `${filtered.length} of ${searchFirst?.total}` : filtered.length} ticket${(searchFirst?.total ?? filtered.length) === 1 ? '' : 's'}`}
            {selectedIds.size > 0 ? (
              <span className="font-medium text-foreground"> · {selectedIds.size} selected</span>
            ) : null}
          </span>
          {selectedIds.size > 0 ? (
            <button
              type="button"
              onClick={clearSelection}
              className="ml-auto cursor-pointer rounded px-1 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Clear selection
            </button>
          ) : null}
        </div>
      ) : null}

      {/* FYI tab: what robot mail is, "Clear all", and senders that look automated. */}
      {activeGroup === 'fyi' ? (
        <FyiLaneBar args={searchBuilt.args} total={searchFirst?.total ?? 0} inboxId={currentInbox.id} />
      ) : null}

      {/* List */}
      <div className={cn('flex flex-col', (searchBusy || listStale) && 'opacity-60 transition-opacity')}>
        {/* `TicketRowSkeletonList` exists in components/Skeletons.tsx and is
            shaped for exactly this list, but the dashboard was hand-rolling its
            own eight-row placeholder — so the busiest surface in the app had a
            different loading language from every other one. */}
        {isLoading ? (
          <TicketRowSkeletonList count={8} />
        ) : filtered.length === 0 && isSearching && !error ? (
          <SearchEmptyState
            query={committedQ.trim()}
            counts={counts}
            activeGroup={activeGroup}
            onJump={selectGroup}
            onClear={() => {
              setQ('');
              setCommittedQ('');
            }}
          />
        ) : filtered.length === 0 ? (
          <DashboardEmptyState
            isFiltered={filtersActive || group !== 'all'}
            hasAny={tickets.length > 0}
            onReset={() => {
              resetFilters();
              setGroup('all');
            }}
          />
        ) : (
          <div
            className="divide-y divide-border/50"
            onClickCapture={
              isSearching
                ? (e) => {
                    if ((e.target as HTMLElement).closest('a')) rememberSearch(committedQ);
                  }
                : undefined
            }
          >
            {filtered.map((t, i) => (
              <div
                key={t.id}
                ref={(el) => { rowRefs.current.set(i, el); }}
                className={cn(
                  'rounded-md transition-colors',
                  i === activeIdx ? 'ring-2 ring-foreground/30 ring-offset-1 ring-offset-background' : '',
                )}
              >
                <TicketRow
                  ticket={t}
                  teamMembersBySlackId={teamMembersBySlackId}
                  selected={selectedIds.has(t.id)}
                  onToggleSelect={() => toggleSelect(t.id)}
                  hasAttachment={attachmentIds.has(t.id)}
                  awayPeriods={awayPeriods}
                  highlightTerms={isSearching ? highlightTerms : undefined}
                  linkSearch={isSearching ? findLinkSearch(highlightTerms, matchById.get(t.id)) : undefined}
                />
                {isSearching && matchById.get(t.id) ? (
                  <SearchSnippet
                    match={matchById.get(t.id) as SearchMatch}
                    terms={highlightTerms}
                    to={`/tickets/${t.id}${findLinkSearch(highlightTerms, matchById.get(t.id))}`}
                    teamMembersBySlackId={teamMembersBySlackId}
                    className="sm:pl-[92px]"
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
        {hasNextPage ? (
          <div
            ref={loadMoreRef}
            className="mt-3 flex items-center justify-center gap-3 text-2xs text-muted-foreground"
          >
            <span className="tabular-nums">
              Showing {filtered.length} of {searchFirst?.total ?? filtered.length}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
        {/* Keyboard hint footer */}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-3xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border/60 bg-muted px-1 py-0.5 font-mono">j</kbd>
            <kbd className="rounded border border-border/60 bg-muted px-1 py-0.5 font-mono">k</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border/60 bg-muted px-1 py-0.5 font-mono">↵</kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border/60 bg-muted px-1 py-0.5 font-mono">/</kbd>
            search
          </span>
        </div>
      </div>
      {selectedIds.size > 0 ? (
        <BulkActionsBar
          selectedIds={Array.from(selectedIds)}
          selectedInboxIds={tickets.filter((t) => selectedIds.has(t.id)).map((t) => t.inbox_id)}
          onClear={clearSelection}
          // Keep just the failures selected so "3 failed — try again" is
          // actionable rather than a dead end.
          onRetainFailed={(ids) => setSelectedIds(new Set(ids))}
        />
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Filter bar helpers — small sub-components kept local to the route to avoid
// fragmenting the codebase for a single consumer. If we ever build a second
// filter bar (e.g. on Contacts), promote them to /components.

function FilterIcon() {
  return (
    <span className="hidden h-6 items-center pl-0.5 pr-1 text-muted-foreground sm:inline-flex">
      <ListFilter className="h-3.5 w-3.5" />
    </span>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="select-none pr-0.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function FilterDivider() {
  return <span aria-hidden className="hidden h-4 w-px bg-border/70 sm:block" />;
}

// Thin wrapper over the shared `EmptyState` primitive. The dashboard used to
// hand-roll this whole block — a bare icon, "No tickets in this view.", and an
// underlined text link — which is the "No results" anti-pattern on the surface
// the team spends its day in. The shared primitive already provides the icon
// chip, the title/subtitle pair and an `action` slot that nothing was using.
function DashboardEmptyState({
  isFiltered,
  hasAny,
  onReset,
}: {
  isFiltered: boolean;
  hasAny: boolean;
  onReset: () => void;
}) {
  const filteredOut = hasAny && isFiltered;
  return (
    <EmptyState
      className="py-16"
      icon={<Inbox className="h-4 w-4" />}
      title={filteredOut ? 'No tickets match these filters' : 'No tickets yet'}
      subtitle={
        filteredOut
          ? 'Every ticket in this inbox is filtered out by the current view. Clear the filters to see them again.'
          : 'New inbound emails land here once the triage workflow has processed them.'
      }
      action={
        filteredOut ? (
          <Button size="sm" variant="outline" onClick={onReset}>
            Clear filters
          </Button>
        ) : undefined
      }
    />
  );
}

// Where a search result's link goes: the ticket, pre-filled with Ctrl+F's
// "find in conversation" (see components/FindInThread.tsx) and, when the hit
// was in a message, that message.
function findLinkSearch(terms: string[], match: SearchMatch | null | undefined): string {
  const p = new URLSearchParams();
  if (terms[0]) p.set('find', terms[0]);
  if (match?.source === 'message' && match.message_id) p.set('m', match.message_id);
  const s = p.toString();
  return s ? `?${s}` : '';
}

// Nothing matched in this tab. The matches are very often in another tab
// (resolved, archived…), so say where they are instead of "no results".
function SearchEmptyState({
  query,
  counts,
  activeGroup,
  onJump,
  onClear,
}: {
  query: string;
  counts: Record<GroupKey, number>;
  activeGroup: GroupKey;
  onJump: (g: GroupKey) => void;
  onClear: () => void;
}) {
  const elsewhere = GROUPS.filter((g) => g.key !== activeGroup && counts[g.key] > 0);
  const label = GROUPS.find((g) => g.key === activeGroup)?.label ?? 'this tab';
  return (
    <EmptyState
      className="py-16"
      icon={<Search className="h-4 w-4" />}
      title={`No matches in ${label}`}
      subtitle={
        elsewhere.length > 0
          ? `"${query}" has matches in other tabs:`
          : `Nothing in this inbox matches "${query}". Try fewer words, or part of a word.`
      }
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          {elsewhere.map((g) => (
            <Button key={g.key} size="sm" variant="outline" onClick={() => onJump(g.key)}>
              {counts[g.key]} in {g.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={onClear}>
            Clear search
          </Button>
        </div>
      }
    />
  );
}

// ─── Sort dropdown ───────────────────────────────────────────────────────────
// Compact pill matching the Views / search bar style. Single-select; closing
// happens automatically when the user picks an option. Active option's label
// renders on the trigger so the current sort is always visible.

function SortDropdown({
  sortBy,
  onChange,
  showRelevance,
}: {
  sortBy: SortKey;
  onChange: (next: SortKey) => void;
  /** "Best match" only makes sense while a search is running. */
  showRelevance: boolean;
}) {
  const options = SORT_OPTIONS.filter((o) => o.key !== 'relevance' || showRelevance);
  const active = options.find((o) => o.key === sortBy) ?? options[0];
  const isDefault = sortBy === 'newest' || sortBy === 'relevance';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-2xs font-medium transition-colors',
            !isDefault
              ? 'border-transparent bg-foreground text-background'
              : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          title="Sort tickets"
        >
          <active.icon className="h-3 w-3" />
          <span className="hidden sm:inline">{active.label}</span>
          <span className="sm:hidden">Sort</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-1">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onSelect={() => onChange(o.key)}
            className={cn(
              'flex flex-col items-start gap-0.5 px-2 py-1.5 text-xs',
              o.key === sortBy && 'bg-accent text-foreground',
            )}
          >
            <span className="flex items-center gap-1.5 font-medium">
              <o.icon className="h-3 w-3 text-muted-foreground" />
              {o.label}
            </span>
            <span className="pl-[18px] text-3xs text-muted-foreground">{o.hint}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
